import dayjs from 'dayjs';
import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQueryClient } from 'react-query';
import { v4 as uuidv4 } from 'uuid';

import { TimePresetMap } from '../../app/constants/time-selection-form';
import { EnsembleContext } from '../../app/context/ensemble-context';
import { useTenant } from '../../app/context/tenant-context';
import { useGetUserViewsV2, useUserId } from '../../app/hooks/accounts';
import { useEnsembleModelsQuery } from '../../app/hooks/tags';
import { useDeleteView } from '../../app/hooks/view';
import { TimeRangeUtil } from '../../app/utils/time';

import { ChartMode } from './investigate-chart-context';
import { useLayoutContext } from './layout-context';

import {
  AnomalySort,
  AssessMode,
  DataModeType,
  Ensemble,
  HiddenTag,
  ParentTag,
  PersistTimeSelection,
  PersistView,
  Tag,
  TimePresetKeys,
  TimeRanges,
  TimeSelection,
  TrendSearchData,
  ViewShareType,
  ViewState,
  ViewType,
} from '@controlrooms/models';

export const chartHeightIncrements = [45, 52, 72, 92, 112, 132, 152, 172, 202];

export const transformToViewState = (
  view: PersistView,
  ensembleFamily: Ensemble | null,
  tenant: number,
  overrideViewId = '',
): ViewState => {
  return {
    userId: view.user_id,
    created_by: view.created_by,
    version: view.version || 1,
    viewId: view.default_view ? 'default' : view.view_id || overrideViewId,
    name: view.view.name || '',
    description: view.view.description || '',
    shared: !!view.shared,
    shareType:
      view.share_type && view.share_type !== ('' as ViewShareType)
        ? view.share_type
        : ViewShareType.PRIVATE,
    isDefault: !!view.default_view,
    selectedEnsemble: ensembleFamily,
    view_id: view.view_id || '',
    selectedMode: view.view.type || ViewType.MONITOR,
    isHidden: false,
    isDirty: false,
    isDeleted: false,
    tenant_id: tenant,
    timeSelection: PersistTimeSelection.toTimeSelection(
      view.view.timeSelection.streamingTimeInSeconds
        ? {
            ...view.view.timeSelection,
            ...TimeRangeUtil.ofDurationV2(view.view.timeSelection.streamingTimeInSeconds),
          }
        : { ...view.view.timeSelection },
    ),
    dataModeType: view.view.dataModeType || DataModeType.AUTO,
    view: {
      ...{
        [ViewType.MONITOR]: {
          severityFilter: 1,
          showLimits: true,
          selectedFolders: [],
          selectedTags: [],
        },
        [ViewType.ANALYZE]: {
          showLimits: true,
          showLabeledEvents: true,
          pinnedTags: [] as ParentTag[],
          hiddenTags: [] as HiddenTag[],
          selectedFolders: [],
          selectedTags: [],
          trendSearchData: {} as TrendSearchData,
        },
        [ViewType.INVESTIGATE]: {
          selectedFolders: [] as number[],
          selectedTags: {} as { [folder: number]: Tag[] },
          showAnomalies: true,
          organize: 'overlays' as ChartMode,
          groupByUom: false,
          showLimits: true,
        },
      },
      ...{
        [view.view.type]: {
          ...(view.view.type === 'monitor' && {
            selectedFolders: view.view.selectedFolders,
            ensemble_family_id: view.view.ensemble_family_id,
            severityFilter: view.view.severityFilter,
            showLimits: view.view.showLimits ?? view.view.showMonitorLimits,
          }),
          ...(view.view.type === 'analyze' && {
            selectedFolders: view.view.selectedFolders,
            ensemble_family_id: view.view.ensemble_family_id,
            showLimits: view.view.showLimits ?? view.view.showAnalyzeLimits,
            showLabeledEvents: view.view.showLabeledEvents,
            pinnedTags: view.view.pinnedTags,
            hiddenTags: view.view.hiddenTags,
            selectedTags: view.view.selectedTags,
            isTrendSearch: view.view.isTrendSearch,
            trendSearchData: view.view.trendSearchData,
          }),
          ...(view.view.type === 'investigate' && {
            ensemble_family_id: view.view.ensemble_family_id,
            selectedFolders: view.view.selectedFolders,
            selectedTags: view.view.selectedTags || {},
            showLimits: view.view.showLimits ?? view.view.showInvestigateLimits,
            showAnomalies: view.view.showAnomalies,
            organize: view.view.chartMode,
            severityFilter: view.view.severityFilter,
            groupByUom: view.view.groupByUom,
          }),
        },
      },
    },
    config: {
      [ViewType.ANALYZE]: {
        chartHeight: view.view.config?.[ViewType.ANALYZE]?.chartHeight || 45,
        limitFilters: view.view.config?.[ViewType.ANALYZE]?.limitFilters || {
          HH: true,
          H: true,
          L: true,
          LL: true,
        },
        sortOption: view.view.config?.[ViewType.ANALYZE]?.sortOption || AnomalySort.SEVERITY,
        assessMode: view.view.config?.[ViewType.ANALYZE]?.assessMode || AssessMode.MODEL,
        modelFilterTypes: view.view.config?.[ViewType.ANALYZE]?.modelFilterTypes || [
          'UNIVARIATE',
          'MULTIVARIATE',
        ],
        showOnlyAlertedModels: view.view.config?.[ViewType.ANALYZE]?.showOnlyAlertedModels || false,
        selectedEnsembleId: view.view.config?.[ViewType.ANALYZE]?.selectedEnsembleId || null,
        browserState: {
          expandedSystems:
            view.view.config?.[ViewType.ANALYZE]?.browserState?.expandedSystems || [],
        },
      },
    },
    timeSelectionHistory: [],
  };
};

export const constructPersistedView = (viewObj: {
  viewState: ViewState;
  viewName: string;
  viewDescription: string;
  shareType: ViewShareType;
  activeModes: {
    [key: string]: ViewType;
  };
  currentUserId: number;
  shared: boolean;
}): PersistView => {
  const { viewState, viewName, viewDescription, activeModes, shareType, currentUserId, shared } =
    viewObj;
  const selectedMode = activeModes[viewState.viewId];
  const persistTimeSelection = PersistTimeSelection.ofTimeSelection(viewState.timeSelection);
  return {
    view: {
      ...viewState.view[selectedMode],
      timeSelection: persistTimeSelection,
      type: selectedMode,
      selectedFolders: viewState.view[selectedMode].selectedFolders,
      name: viewName,
      ensemble_family_id: viewState?.selectedEnsemble?.family_id,
      showLimits: viewState.view[selectedMode].showLimits,
      showLabeledEvents: viewState.view[selectedMode].showLabeledEvents,
      dataModeType: viewState.dataModeType,
      description: viewDescription,
      config: viewState.config,
    },
    view_name: viewName,
    description: viewDescription,
    default_view: !!viewState.isDefault,
    user_id: currentUserId,
    shared,
    share_type: shareType,
  };
};

interface ViewContextProps {
  viewId: string;
  viewState: ViewState;
  setViewState: Dispatch<SetStateAction<ViewState>>;
  resetTenantViewState: () => void;
  handleShowLimits: (showLimits: boolean | undefined) => void;
  handleShowLabeledEvents: (showEvents: boolean | undefined) => void;
  closeView: () => void;
  deleteView: () => void;
  duplicateView: () => void;
  handleSystemSelection: (systems: number[]) => void;
  updateTimeSelection: (timeselection: TimeSelection, makeDirty?: boolean) => void;
  handleIncrementAssessChartHeight: () => void;
  handleDecrementAssessChartHeight: () => void;
  recordTimelineHistory: () => void;
  updateLocalView: (viewName: string, view: ViewState) => void;
  searchMode: boolean;
  setSearchMode: Dispatch<SetStateAction<boolean>>;
  setTrendSearchData: (trendSearchData: TrendSearchData, isDirty?: boolean) => void;
  clearTrendSearchData: () => void;
  getQueryKeyWithViewId: (label: string) => string;
  handleUndoTimeSelection: () => void;
}

const defaultState = {
  viewId: 'default',
  viewState: {} as ViewState,
  setViewState: () => null,
  resetTenantViewState: () => null,
  handleIncrementAssessChartHeight: () => null,
  handleDecrementAssessChartHeight: () => null,
  handleShowLimits: () => null,
  handleShowLabeledEvents: () => null,
  closeView: () => null,
  deleteView: () => null,
  duplicateView: () => null,
  handleSystemSelection: () => null,
  updateTimeSelection: () => null,
  recordTimelineHistory: () => null,
  updateLocalView: () => null,
  searchMode: false,
  setSearchMode: () => null,
  setTrendSearchData: () => null,
  clearTrendSearchData: () => null,
  getQueryKeyWithViewId: (label: string) => `${label}-default`,
  handleUndoTimeSelection: () => null,
};

export const ViewContext = createContext<ViewContextProps>(defaultState);

export const ViewContextProvider: React.FC<{
  viewId: string;
  children: ReactNode;
  tempNode?: {
    viewType: string;
    referenceId: string;
    state: {
      selectedFolders: string;
    };
  };
}> = ({ viewId, children, tempNode }) => {
  const { tenant: currentTenantId } = useTenant();
  const currentUserId = useUserId();
  const queryClient = useQueryClient();

  const { ensembleFamilies } = useEnsembleModelsQuery();
  const { selectedEnsemble } = useContext(EnsembleContext);
  const { activeModes, activeView, setViewIds, setActiveModes, setActiveView, setSavedViewIds } =
    useLayoutContext();

  const { data: userViews } = useGetUserViewsV2();
  const persistDefaultView = userViews?.filter((v) => v.default_view === true)[0];

  const defaultTimeSelections: TimeSelection = useMemo(
    () => ({
      startTime: dayjs().subtract(12, 'hour'),
      endTime: dayjs(),
      timezone: dayjs.tz.guess(), // Default to user timezone
      timeRange: TimeRanges.PRESET,
      streamingTimeInSeconds: TimePresetMap.get(TimePresetKeys.LAST_TWELVE_HOURS), // TODO: need to set current tenant default time selection
      nowSelected: false,
    }),
    [],
  );

  const defaultViewState: ViewState = useMemo(
    () => ({
      viewId,
      version: 2,
      userId: currentUserId,
      name: viewId === 'default' ? 'default' : '',
      description: '',
      shared: false,
      selectedMode: tempNode?.viewType === 'ALERT_LINK' ? ('' as ViewType) : ViewType.MONITOR,
      isHidden: false,
      isDefault: viewId === 'default',
      isDirty: false,
      isDeleted: false,
      tenant_id: currentTenantId,
      timeSelection: defaultTimeSelections,
      dataModeType: DataModeType.AUTO,
      view: {
        [ViewType.MONITOR]: {
          type: ViewType.MONITOR,
          severityFilter: 0,
          showLimits: true,
          pinnedTags: [],
          hiddenTags: [],
          selectedFolders: [],
          selectedTags: [],
        },
        [ViewType.ANALYZE]: {
          type: ViewType.ANALYZE,
          selectedTags: [],
          showLimits: true,
          showLabeledEvents: true,
          pinnedTags: [] as ParentTag[],
          hiddenTags: [] as HiddenTag[],
          selectedFolders:
            tempNode?.state.selectedFolders?.split(',').map(Number) || ([] as number[]),
          trendSearchData: {} as TrendSearchData,
          isTrendSearch: false,
        },
        [ViewType.INVESTIGATE]: {
          selectedFolders: [] as number[],
          selectedTags: {} as { [folder: number]: Tag[] },
          showAnomalies: true,
          organize: 'overlays' as ChartMode,
          groupByUom: false,
          showLimits: true,
        },
      },
      config: {
        [ViewType.ANALYZE]: {
          chartHeight: 45,
          limitFilters: {
            HH: true,
            H: true,
            L: true,
            LL: true,
          },
          sortOption: AnomalySort.SEVERITY,
          assessMode: AssessMode.MODEL,
          modelFilterTypes: ['UNIVARIATE'],
          showOnlyAlertedModels: false,
          selectedEnsembleId: null,
          browserState: {
            expandedSystems: [],
          },
        },
      },
      view_id: '',
      timeSelectionHistory: [],
      selectedEnsemble: selectedEnsemble,
    }),
    [
      viewId,
      currentUserId,
      tempNode?.viewType,
      tempNode?.state.selectedFolders,
      currentTenantId,
      defaultTimeSelections,
      selectedEnsemble,
    ],
  );

  // Retrieve state from session storage or use default
  const [viewState, setViewState] = useState<ViewState>(() => {
    const savedViewState = sessionStorage.getItem(viewId);
    if (savedViewState) {
      const parsedViewState: ViewState = JSON.parse(savedViewState);
      parsedViewState.timeSelection.startTime = dayjs(parsedViewState.timeSelection.startTime);
      parsedViewState.timeSelection.endTime = dayjs(parsedViewState.timeSelection.endTime);
      return parsedViewState;
    }
    if (viewId === 'default' && persistDefaultView) {
      const ensembleFamily = ensembleFamilies.find(
        (e) => e.family_id === persistDefaultView?.view?.ensemble_family_id,
      );

      const transformedView = transformToViewState(
        persistDefaultView,
        ensembleFamily || null,
        currentTenantId,
      );

      setActiveModes((prev) => ({
        ...prev,
        ['default']: persistDefaultView.view.type || prev['default'] || ViewType.MONITOR,
      }));
      transformedView.viewId = 'default';
      return transformedView;
    }
    return defaultViewState;
  });

  // const { mutateAsync: createView } = useSaveView();
  const { mutateAsync: deletePersistedView } = useDeleteView();
  // const { mutateAsync: updatePersistedView } = useUpdateView();
  const [searchMode, setSearchMode] = useState(defaultState.searchMode);

  const resetTenantViewState = useCallback(() => {
    sessionStorage.clear();
    setViewState(defaultViewState);
  }, [defaultViewState]);

  // Retrieve state from session storage or use default
  // const [viewState, setViewState] = useState<ViewState>(() => {
  //   const savedViewState = sessionStorage.getItem(viewId);
  //   if (savedViewState) {
  //     const parsedViewState: ViewState = JSON.parse(savedViewState);
  //     parsedViewState.timeSelection.startTime = dayjs(parsedViewState.timeSelection.startTime);
  //     parsedViewState.timeSelection.endTime = dayjs(parsedViewState.timeSelection.endTime);
  //     return parsedViewState;
  //   }
  //   if (viewId === 'default') {
  //     defaultViewState.isNewDefault = true;
  //   }
  //   return defaultViewState;
  // });

  // Persist state to session storage whenever it changes
  useEffect(() => {
    try {
      sessionStorage.setItem(viewId, JSON.stringify(viewState));
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === 'QuotaExceededError') {
        console.error('Quota exceeded, trimming view state', viewId, viewState);
      }
    }
  }, [viewId, viewState]);

  useEffect(() => {
    if (viewState.view_id !== 'default' && viewState.view_id !== '') {
      setSavedViewIds((prev) => {
        const savedViewIds = Array.from(new Set([...prev, ...[viewState.view_id]]));
        return viewId === 'default' ? prev : savedViewIds;
      });
    }
  }, [persistDefaultView, setSavedViewIds, viewId, viewState]);

  const handleIncrementAssessChartHeight = useCallback(() => {
    setViewState((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        [ViewType.ANALYZE]: {
          ...prev.config?.[ViewType.ANALYZE],
          chartHeight: (() => {
            const currentHeight =
              prev.config?.[ViewType.ANALYZE]?.chartHeight ?? chartHeightIncrements[0];
            const currentIndex = chartHeightIncrements.indexOf(currentHeight);
            const nextIndex = Math.min(currentIndex + 1, chartHeightIncrements.length - 1);
            return chartHeightIncrements[nextIndex];
          })(),
        },
      },
    }));
  }, []);

  const handleDecrementAssessChartHeight = useCallback(() => {
    setViewState((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        [ViewType.ANALYZE]: {
          ...prev.config?.[ViewType.ANALYZE],
          chartHeight: (() => {
            const currentHeight =
              prev.config?.[ViewType.ANALYZE]?.chartHeight ?? chartHeightIncrements[0];
            const currentIndex = chartHeightIncrements.indexOf(currentHeight);
            const nextIndex = Math.max(currentIndex - 1, 0);
            return chartHeightIncrements[nextIndex];
          })(),
        },
      },
    }));
  }, []);

  const handleShowLimits = useCallback(
    (newShowLimitsValue: boolean | undefined) => {
      setViewState((prevState) => {
        const selectedMode = activeModes[activeView];
        return {
          ...prevState,
          isDirty: true,
          view: {
            ...prevState.view,
            [selectedMode]: {
              ...prevState.view[selectedMode],
              showLimits: !!newShowLimitsValue,
            },
          },
        };
      });
    },
    [activeModes, activeView],
  );
  const handleShowLabeledEvents = useCallback(
    (showLabeledEvents: boolean | undefined) => {
      setViewState((prevState) => {
        const selectedMode = activeModes[activeView];
        return {
          ...prevState,
          isDirty: true,
          view: {
            ...prevState.view,
            [selectedMode]: {
              ...prevState.view[selectedMode],
              showLabeledEvents: !!showLabeledEvents,
            },
          },
        };
      });
    },
    [activeModes, activeView],
  );

  // Close view
  const closeView = useCallback(() => {
    if (viewState.viewId === 'default') return;

    if (activeView === viewState.viewId) {
      setActiveView('default');
    }
    setViewIds((prev) => {
      const newState = [...prev];
      const index = newState.indexOf(viewState.viewId);
      newState.splice(index, 1);
      return newState;
    });
    setSavedViewIds((prev) => {
      const newState = [...prev];
      const index = newState.indexOf(viewState.viewId);
      newState.splice(index, 1);
      return viewId === 'default' ? prev : newState;
    });
    sessionStorage.removeItem(viewId);
    queryClient.invalidateQueries(['user-views', currentUserId]);
  }, [
    viewState.viewId,
    activeView,
    setViewIds,
    setSavedViewIds,
    viewId,
    queryClient,
    currentUserId,
    setActiveView,
  ]);

  // Update time selection in view
  const updateTimeSelection = useCallback((newTimeselection: TimeSelection, makeDirty = false) => {
    setViewState((prev) => ({
      ...prev,
      isDirty: makeDirty !== undefined ? makeDirty : prev.isDirty,
      timeSelection: newTimeselection,
    }));
  }, []);

  // Update and record the time selection history
  const recordTimelineHistory = useCallback(() => {
    setViewState((prev) => ({
      ...prev,
      isDirty: true,
      timeSelectionHistory: [...prev.timeSelectionHistory, prev.timeSelection],
    }));
  }, []);

  const updateLocalView = useCallback((viewName: string, view: ViewState) => {
    setViewState((prev) => {
      const newState = { ...prev, ...view };
      return newState;
    });
  }, []);

  const handleSystemSelection = useCallback(
    (systems: number[]) => {
      const selectedMode = activeModes[viewId];
      setViewState((prev) => {
        const newState = { ...prev };
        newState.isDirty = true;
        newState.view[selectedMode].selectedFolders = systems;
        return newState;
      });
    },
    [activeModes, viewId],
  );

  const setTrendSearchData = useCallback((trendSearchData: TrendSearchData, isDirty?: boolean) => {
    setViewState((prev) => {
      const newState = { ...prev };
      newState.view[ViewType.ANALYZE].trendSearchData = trendSearchData;
      newState.view[ViewType.ANALYZE].isTrendSearch = true;
      newState.isDirty = isDirty ?? false;
      return newState;
    });
  }, []);

  const clearTrendSearchData = useCallback(() => {
    setViewState((prev) => {
      const newState = { ...prev };
      newState.view[ViewType.ANALYZE].isTrendSearch = false;
      delete newState.view[ViewType.ANALYZE].trendSearchData;
      return newState;
    });
  }, []);

  // Delete view
  const deleteView = useCallback(async () => {
    await deletePersistedView(viewState.view_id);
    //Remove from active views
    if (activeView === viewState.viewId) {
      setActiveView('default');
    }
    //Remove from active modes
    setActiveModes((prev) => {
      const newState = { ...prev };
      delete newState[viewState.viewId];
      return newState;
    });
    //Remove from viewIds
    setViewIds((prev) => {
      const newState = [...prev];
      const index = newState.indexOf(viewState.viewId);
      newState.splice(index, 1);
      return newState;
    });
    //Remove from sessionStorage
    sessionStorage.removeItem(viewState.viewId);
  }, [
    activeView,
    deletePersistedView,
    setActiveModes,
    setActiveView,
    setViewIds,
    viewState.viewId,
    viewState.view_id,
  ]);

  // Duplicate view
  const duplicateView = useCallback(() => {
    const uuid = uuidv4();
    const currentSessionView = sessionStorage.getItem(viewId);
    if (!currentSessionView) {
      console.error('No view found with the viewid', viewId);
      return;
    }

    const currView: ViewState = JSON.parse(currentSessionView);
    currView.userId = currentUserId;
    currView.isDirty = false;
    currView.viewId = uuid;
    currView.view_id = '';
    currView.isDefault = false;
    currView.name = 'Copy of ' + currView.name;
    currView.description = '';
    sessionStorage.setItem(uuid, JSON.stringify(currView));
    setViewIds((prevItems) => [...prevItems, uuid]);
    setActiveModes((prev) => ({ ...prev, [uuid]: activeModes[viewId] }));
    setActiveView(uuid);
  }, [activeModes, currentUserId, setActiveModes, setActiveView, setViewIds, viewId]);

  const getQueryKeyWithViewId = useCallback((label: string) => `${label}-${viewId}`, [viewId]);

  const handleUndoTimeSelection = useCallback(() => {
    const resetTo = viewState.timeSelectionHistory.pop();
    if (resetTo) {
      setViewState((prev) => ({
        ...prev,
        isDirty: true,
        timeSelection: {
          ...resetTo,
          startTime: dayjs(resetTo.startTime),
          endTime: dayjs(resetTo.endTime),
        },
        timeSelectionHistory: [...viewState.timeSelectionHistory],
      }));
    }
  }, [viewState.timeSelectionHistory, setViewState]);

  const appState = useMemo(
    () => ({
      viewId,
      viewState,
      setViewState,
      resetTenantViewState,
      handleShowLimits,
      handleShowLabeledEvents,
      closeView,
      deleteView,
      duplicateView,
      handleIncrementAssessChartHeight,
      handleDecrementAssessChartHeight,
      updateTimeSelection,
      recordTimelineHistory,
      handleSystemSelection,
      updateLocalView,
      searchMode,
      setSearchMode,
      setTrendSearchData,
      clearTrendSearchData,
      getQueryKeyWithViewId,
      handleUndoTimeSelection,
    }),
    [
      viewId,
      viewState,
      resetTenantViewState,
      handleShowLimits,
      handleShowLabeledEvents,
      closeView,
      deleteView,
      duplicateView,
      handleIncrementAssessChartHeight,
      handleDecrementAssessChartHeight,
      updateTimeSelection,
      recordTimelineHistory,
      handleSystemSelection,
      updateLocalView,
      searchMode,
      setTrendSearchData,
      clearTrendSearchData,
      getQueryKeyWithViewId,
      handleUndoTimeSelection,
    ],
  );

  return <ViewContext.Provider value={appState}>{children}</ViewContext.Provider>;
};

export const useViewContext = (): ViewContextProps => {
  const context = useContext(ViewContext);
  if (!context) {
    throw new Error('useViewContext must be used within a ViewContextProvider');
  }
  return context;
};
